/**
 * @file metrics.api.js
 * @module MetricsAPI
 * @description HTTP API wrapper for metrics endpoints (analytics, reports). Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const MetricsAPI = {
    /**
     * Get stats card data.
     * @param {string} filter - Filter identifier for stats.
     * @param {string} start - Start date (ISO or backend-expected format).
     * @param {string} end - End date (ISO or backend-expected format).
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to stats card data.
     */
    getStatsCard: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/statscard?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get line reservation data for charting.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to time-series data.
     */
    getLineReservation: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/linereservation?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get pie chart aggregated data.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to pie chart data.
     */
    GetPieChart: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/piechart?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get histogram data.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to histogram data.
     */
    getHistograms: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/histograms?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get status list for dashboard.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to status list data.
     */
    getStatusList: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/status?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get top suppliers.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to top suppliers data.
     */
    getTopsSuppliers: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/tops?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get top countries.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to top countries data.
     */
    getTopsCountries: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/tops/countries?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get income information.
     * @param {string} filter - Filter identifier.
     * @param {string} start - Start date.
     * @param {string} end - End date.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to income metrics.
     */
    getIncome: (filter, start, end, token = "") => {
        return axiosInstance(token).get(`/dashboard/income/?filter=${filter}&start=${start}&end=${end}`)
    },

    /**
     * Get badges (items values) for dashboard.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to badge values.
     */
    getBadges: (token = "") => {
        return axiosInstance(token).get(`/dashboard/items-values`)
    },
}